import React, { Fragment, useCallback, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { useTranslation } from '@sunbit-dev/translations';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { StyledLink } from '@sunbit-dev/web-ui-lib';
import { LegalAuthorizationProps, HealthcareLegal } from './models';
import { LINK_MAP, HEALTHCARE_COLLAPSED_KEYS, HEALTHCARE_EXPANDED_KEYS } from './constants';
import * as styles from './styles';
import { getStore } from '~/store/reducers/purchase/purchase';
import URLs from '~/enums/URLs';
import expandMoreIcon from '../../../../../../../../assets/expand-more.svg';
import expandLessIcon from '../../../../../../../../assets/expand-less.svg';

export const LegalAuthorization = styled(({ className }: LegalAuthorizationProps) => {
  const { t } = useTranslation('landing_page', { keyPrefix: 'welcome_view.legal_authorization' });
  const { purchaseInfo } = useSelector(getStore);
  const history = useHistory();
  const { search } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  const renderSegment = useCallback(
    (text: string, index: number) => {
      const config = LINK_MAP[text];
      if (!config) return <Fragment key={index}>{text}</Fragment>;

      if (config.type === 'email') {
        return (
          <StyledLink key={index} className="link" href={config.href}>
            {text}
          </StyledLink>
        );
      }

      const onClick = () => history.replace({ pathname: [URLs.Info, config.url].join(''), search });

      return (
        <StyledLink key={index} className="link" onClick={onClick} data-testid={config.testId}>
          {text}
        </StyledLink>
      );
    },
    [history, search]
  );

  const renderBullets = (keys: readonly string[], healthcare: HealthcareLegal) =>
    keys.map((key) => <li key={key}>{healthcare[key].map(renderSegment)}</li>);

  const defaultText = (t('default', { returnObjects: true }) as string[]).map(renderSegment);

  if (!purchaseInfo.isHealthcareVertical) {
    return <div className={className}>{defaultText}</div>;
  }

  const healthcare = t('healthcare', { returnObjects: true }) as HealthcareLegal;

  return (
    <div className={classNames(className, 'healthcare')}>
      <div className="text-wrapper">
        <p>{defaultText}</p>
        {healthcare.title}
        <ul className="legal-list">
          {renderBullets(HEALTHCARE_COLLAPSED_KEYS, healthcare)}
          {isExpanded && renderBullets(HEALTHCARE_EXPANDED_KEYS, healthcare)}
        </ul>
      </div>
      <div className="toggle-button" onClick={toggleExpanded}>
        <img src={isExpanded ? expandLessIcon : expandMoreIcon} alt="expand-icon" />
        {isExpanded ? t('close_full') : t('view_full')}
      </div>
    </div>
  );
})`
  ${styles.LegalAuthorization}
`;
